import {
    Button,
    Checkbox,
    FieldGroup,
    Loading,
    TabUI,
    TextField,
    Typography,
    useAlert
} from "matsuri-ui"
import { css } from "@emotion/react"
import { sort } from "fast-sort"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import React, { useCallback, useMemo, useState } from "react"
import {
    requestAssignCleaner,
    requestAssignCompany,
    requestGetAssignedCleanersByCleaningId,
    requestUnassignCleaner,
    requestUnassignCompany,
    useCleaning,
    useCleaningCleaners
} from "../../../hooks/useCleanings"
import { sleep } from "../../../helpers/sleep"
import { useCompanyAssignment } from "@/hooks/useCompanyAssignment"
import { useOperation } from "@/hooks/useOperation"
import {
    ApprovalRequest,
    useApprovalRequestsByOperationIds
} from "@/hooks/useApprovalRequestsByOperationIds"
import { CleanerTags } from "@/components/CleanerTags"
import { CompanyTag } from "@/components/CompanyTag"
import { InspectionField } from "@/components/InspectionField"
import { PLACEMENT_TYPES } from "@/domain/placement"
import { RejectedLabel } from "@/pages/CleaningDetail/parts/RejectedLabel"
import { useAreaViewTab } from "@/hooks/useAreaViewTab"
import { useGetCleanersWithTags } from "@/hooks/useCleanerTag"

export type AssignableCleaning = {
    id: string
    cleanerNames: string[]
    cleanerIds?: string[]
    cleaningCompanyId?: string
    cleaningCompanyName?: string
} & FieldsByPlacementType

export type FieldsByPlacementType =
    | {
          placementType: (typeof PLACEMENT_TYPES)["commonArea"]
          commonAreaName: string
      }
    | {
          placementType: (typeof PLACEMENT_TYPES)["listing"]
          listingName: string
      }

export const AssignPanel = ({
    cleanings
}: {
    cleanings: AssignableCleaning[]
}) => {
    const { token } = useAuth()

    const { selectedCityGroupId, onCityGroupIdSelected, areaTabs } =
        useAreaViewTab()
    const { data: cleaners } = useGetCleanersWithTags(
        token,
        selectedCityGroupId
    )
    const { canSelectCompany, assignableCompanies } = useCompanyAssignment()

    const { throwAlert } = useAlert()

    const [filterCleanerQuery, setFilterCleanerQuery] = useState("")
    const [processingUnassignAll, setProcessingUnassignAll] = useState(false)

    // 選択された清掃が単一の場合のみ、割り当ての解除ができるようにする
    // (テーブルから選択せずいきなりパネルをオープンするようなケースでは、割り当て解除などができる方が自然であるため)
    const isSingle = cleanings.length === 1
    const cleaningId = isSingle ? cleanings[0].id : undefined

    // 共用部清掃かどうかを判定
    const isCommonArea =
        cleanings.length > 0 && cleanings[0].placementType === "commonArea"

    const { data: assigned, refetch } = useCleaningCleaners(token, cleaningId)
    const { onUpdate, closePanel } = useAssignPanelContext()

    // 清掃詳細から業者情報を取得（単一清掃の場合のみ）
    // 物件清掃の場合はuseCleaning、共用部清掃の場合はuseOperationを使用
    const { data: cleaningDetail, refetch: refetchCleaningDetail } =
        useCleaning(token, !isCommonArea ? cleaningId : undefined)

    const { data: operationDetail, refetch: refetchOperationDetail } =
        useOperation(token, isCommonArea ? cleaningId : undefined)

    // placementTypeに応じて業者IDを取得
    const assignedCompanyId = isCommonArea
        ? operationDetail?.cleaningCompanyId
        : cleaningDetail?.cleaningCompanyId
    const { data: approvalRequest } = useApprovalRequestsByOperationIds(
        token,
        cleaningId ? [cleaningId] : []
    )

    const approvalRequestByCleanerId: Record<string, ApprovalRequest> =
        useMemo(() => {
            if (!approvalRequest || !cleaningId) return {}

            const approvalRequests = approvalRequest[cleaningId]

            return approvalRequests
        }, [approvalRequest, cleaningId])

    const [progress, setProgress] = useState<Set<string>>(new Set())

    // 清掃の最新業者IDを取得するヘルパー関数
    const getCurrentCompanyId = useCallback(
        (cleaning: AssignableCleaning) => {
            // 単一選択の場合は最新の業者情報を使用、複数選択の場合はプロップの値を使用
            return isSingle && cleaning.id === cleaningId
                ? assignedCompanyId
                : cleaning.cleaningCompanyId
        },
        [isSingle, cleaningId, assignedCompanyId]
    )

    // 共通してアサインされている清掃員IDを検出
    const commonCleanerIds = useMemo(() => {
        if (cleanings.length === 0) return []
        if (isSingle) return [] // 単一選択時は使用しない

        const firstCleanerIds = cleanings[0].cleanerIds ?? []
        return firstCleanerIds.filter(id =>
            cleanings.every(c => c.cleanerIds?.includes(id))
        )
    }, [cleanings, isSingle])

    // 共通してアサインされている業者IDを検出
    const commonCompanyId = useMemo(() => {
        if (cleanings.length === 0) return undefined
        if (isSingle) return assignedCompanyId // 単一選択時は既存ロジック

        const firstCompanyId = cleanings[0].cleaningCompanyId
        if (!firstCompanyId) return undefined

        return cleanings.every(c => c.cleaningCompanyId === firstCompanyId)
            ? firstCompanyId
            : undefined
    }, [cleanings, isSingle, assignedCompanyId])

    // 業者と清掃員を統合したリスト（業者が先頭）
    type AssignableItem =
        | {
              type: "company"
              id: string
              name: string
          }
        | {
              type: "cleaner"
              id: string
              name: string
              email: string
              tags: { id: string; name: string }[]
          }

    const filteredAssignableItems = useMemo(() => {
        const query = filterCleanerQuery.trim().toLowerCase()

        // 業者リスト（業者アサイン機能が有効な場合のみ）
        const companyItems: AssignableItem[] = canSelectCompany
            ? assignableCompanies
                  .filter(
                      company =>
                          query === "" ||
                          (company.name || "").toLowerCase().includes(query)
                  )
                  .map(company => ({
                      type: "company" as const,
                      id: company.id,
                      name: company.name
                  }))
            : []

        // 清掃員リスト（ソート済み）
        const sortedCleaners = sort(cleaners ?? []).asc(entry => entry.name)
        const cleanerItems: AssignableItem[] = sortedCleaners
            .filter(
                cleaner =>
                    query === "" ||
                    (cleaner.name || "").toLowerCase().includes(query) ||
                    (cleaner.email || "").toLowerCase().includes(query)
            )
            .map(cleaner => ({
                type: "cleaner" as const,
                id: cleaner.id,
                name: cleaner.name,
                email: cleaner.email,
                tags: cleaner.tags
            }))

        // 業者を先頭に配置
        return [...companyItems, ...cleanerItems]
    }, [canSelectCompany, assignableCompanies, cleaners, filterCleanerQuery])

    // アサイン/アンアサイン処理
    const handleAssignChange = useCallback(
        async (item: AssignableItem, shouldAssign: boolean) => {
            const progressKey =
                item.type === "company"
                    ? `company:${item.id}`
                    : `cleaner:${item.id}`

            setProgress(prev => new Set(prev).add(progressKey))

            const errors: Error[] = []
            await Promise.all(
                cleanings.map(async cleaning => {
                    let error: Error | undefined

                    if (item.type === "company") {
                        const result = shouldAssign
                            ? await requestAssignCompany(
                                  token,
                                  cleaning.id,
                                  item.id
                              )
                            : await requestUnassignCompany(token, cleaning.id)
                        error = result.error
                    } else {
                        const result = shouldAssign
                            ? await requestAssignCleaner(
                                  token,
                                  cleaning.id,
                                  item.id
                              )
                            : await requestUnassignCleaner(
                                  token,
                                  cleaning.id,
                                  item.id
                              )
                        error = result.error
                    }

                    if (error) {
                        errors.push(error)
                    }
                })
            )

            throwAlert(
                errors.length > 0 ? new Error("エラーがあります") : undefined,
                {
                    errorMessage: "割り当てに失敗しました",
                    ignoreSuccess: true
                }
            )
            await refetch()
            // placementTypeに応じて適切なrefetchを呼び出す
            if (isCommonArea) {
                await refetchOperationDetail()
            } else {
                await refetchCleaningDetail()
            }
            onUpdate()
            setProgress(prev => {
                const newSet = new Set(prev)
                newSet.delete(progressKey)
                return newSet
            })
        },
        [
            cleanings,
            token,
            throwAlert,
            refetch,
            refetchCleaningDetail,
            refetchOperationDetail,
            onUpdate,
            isCommonArea
        ]
    )

    // 「割り当てを全て解除」ボタンのハンドラ
    const handleUnassignAll = useCallback(async () => {
        // 業者アサイン済みと清掃員のみの清掃を分類
        const companyAssignments = cleanings.filter(
            c => canSelectCompany && getCurrentCompanyId(c)
        )
        const cleanerOnlyAssignments = cleanings.filter(
            c => !canSelectCompany || !getCurrentCompanyId(c)
        )

        // 状況に応じたメッセージを生成
        const confirmMessage = (() => {
            if (
                companyAssignments.length > 0 &&
                cleanerOnlyAssignments.length > 0
            ) {
                return `${cleanings.length}件の清掃の割り当てを解除します（業者: ${companyAssignments.length}件、清掃員のみ: ${cleanerOnlyAssignments.length}件）。この操作は取り消せません。よろしいですか？`
            } else if (companyAssignments.length > 0) {
                return `${cleanings.length}件の清掃の業者割り当てを解除します。この操作は取り消せません。よろしいですか？`
            } else {
                return `${cleanings.length}件の清掃の清掃員割り当てを解除します。この操作は取り消せません。よろしいですか？`
            }
        })()

        const ok = window.confirm(confirmMessage)
        if (!ok) {
            return
        }

        setProcessingUnassignAll(true)

        const errors: Error[] = []
        await Promise.all(
            cleanings.map(async cleaning => {
                const currentCompanyId = getCurrentCompanyId(cleaning)

                // Matsuriユーザーで業者がアサインされている場合は業者をアンアサイン
                if (canSelectCompany && currentCompanyId) {
                    const { error } = await requestUnassignCompany(
                        token,
                        cleaning.id
                    )
                    if (error) {
                        errors.push(error)
                    }
                    // 業者アンアサイン時は清掃員は解除しない
                    // （業者の清掃員は業者自身のみが操作可能）
                } else {
                    // 業者がアサインされていない場合は清掃員をアンアサイン
                    const { data: cleaners, error } =
                        await requestGetAssignedCleanersByCleaningId(
                            token,
                            cleaning.id
                        )
                    if (error) {
                        errors.push(error)
                        return
                    }
                    if (!cleaners) {
                        return
                    }

                    await Promise.all(
                        cleaners.map(async cleaner => {
                            const { error } = await requestUnassignCleaner(
                                token,
                                cleaning.id,
                                cleaner.id
                            )
                            if (error) {
                                errors.push(error)
                            }
                        })
                    )
                }
            })
        )

        // 上記のAPIの処理が早すぎてボタンの変更が見えないことがあるので、ちょっと待つ
        await sleep(500)

        setProcessingUnassignAll(false)

        throwAlert(
            errors.length > 0 ? new Error("エラーがあります") : undefined,
            {
                errorMessage: {
                    happend: "割り当ての解除に失敗しました",
                    reason: errors.join(",")
                },
                successMessage: {
                    happend: "割り当てを解除しました"
                }
            }
        )
        await refetch()
        // placementTypeに応じて適切なrefetchを呼び出す
        if (isCommonArea) {
            await refetchOperationDetail()
        } else {
            await refetchCleaningDetail()
        }
        onUpdate()
    }, [
        cleanings,
        canSelectCompany,
        getCurrentCompanyId,
        token,
        throwAlert,
        refetch,
        refetchCleaningDetail,
        refetchOperationDetail,
        onUpdate,
        isCommonArea
    ])

    return (
        <FieldGroup
            key={JSON.stringify(cleanings.map(c => c.id))}
            loading={isSingle && !assigned}
        >
            <div
                css={css`
                    display: grid;
                    gap: 32px;
                `}
            >
                <div
                    css={css`
                        display: grid;
                        gap: 8px;
                    `}
                >
                    <Typography>{cleanings.length}件の清掃割り当て</Typography>
                    <Typography
                        variant="h3"
                        css={css`
                            display: grid;
                            gap: 16px;
                        `}
                    >
                        {cleanings.map((c, index) => (
                            <span key={index}>
                                {c.placementType === "commonArea"
                                    ? c.commonAreaName
                                    : c.listingName}
                            </span>
                        ))}
                    </Typography>
                </div>

                <section>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleUnassignAll}
                        disabled={processingUnassignAll}
                    >
                        {processingUnassignAll ? (
                            <Loading />
                        ) : (
                            <>割り当てを全て解除</>
                        )}
                    </Button>
                </section>
                {isSingle && cleaningId && (
                    <InspectionField
                        cleaningId={cleaningId}
                        cleaningCompanyId={
                            isCommonArea
                                ? operationDetail?.cleaningCompanyId
                                : cleaningDetail?.cleaningCompanyId
                        }
                        hasInspection={
                            isCommonArea
                                ? operationDetail?.hasInspection
                                : cleaningDetail?.hasInspection
                        }
                        onSaved={() => {
                            if (isCommonArea) {
                                void refetchOperationDetail()
                            } else {
                                void refetchCleaningDetail()
                            }
                            onUpdate()
                        }}
                    />
                )}
                <section
                    css={css`
                        display: grid;
                        gap: 16px;
                    `}
                >
                    <Typography variant="h4">
                        {canSelectCompany
                            ? "清掃員または業者を割り当てる"
                            : "清掃員を割り当てる"}
                    </Typography>

                    <div
                        css={css`
                            display: grid;
                            grid-template-columns: 1fr auto;
                            gap: 8px;
                            align-items: flex-end;
                        `}
                    >
                        <TextField
                            label={
                                canSelectCompany
                                    ? "清掃員または業者をフィルタ"
                                    : "清掃員をフィルタ"
                            }
                            placeholder="名前、メールアドレスを入力 (部分一致)"
                            onChange={value => setFilterCleanerQuery(value)}
                            value={filterCleanerQuery}
                        />
                        <Button
                            onClick={() => {
                                setFilterCleanerQuery("")
                            }}
                        >
                            リセット
                        </Button>
                    </div>
                    <div>
                        <Typography
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            清掃担当者:{" "}
                        </Typography>

                        {assigned
                            ?.filter(a => a.name && a.name.trim() !== "")
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(cleaner => (
                                <Typography key={cleaner.id}>
                                    {cleaner.name}
                                </Typography>
                            ))}
                    </div>

                    <TabUI
                        variant="buttons"
                        tabs={areaTabs}
                        onChange={index =>
                            onCityGroupIdSelected(areaTabs[index].data?.id)
                        }
                        defaultIndex={0}
                        tabPosition="start"
                    >
                        <div
                            key={cleaningId}
                            css={css`
                                display: grid;
                                gap: 8px;
                            `}
                        >
                            {(() => {
                                // 業者がアサインされているかどうか（cleaningsから取得）
                                const hasAssignedCompany = isSingle
                                    ? !!assignedCompanyId
                                    : cleanings.some(c => !!c.cleaningCompanyId) // 1件でも業者アサイン済みなら true
                                // 清掃員がアサインされているかどうか
                                const hasAssignedCleaners = isSingle
                                    ? assigned && assigned.length > 0
                                    : cleanings.some(
                                          c =>
                                              c.cleanerIds &&
                                              c.cleanerIds.length > 0
                                      ) // 1件でも清掃員アサイン済みなら true

                                return filteredAssignableItems.map(item => {
                                    const progressKey =
                                        item.type === "company"
                                            ? `company:${item.id}`
                                            : `cleaner:${item.id}`

                                    const approvalRequestStatus =
                                        item.type === "cleaner"
                                            ? approvalRequestByCleanerId[
                                                  item.id
                                              ]?.status
                                            : undefined

                                    const isAssigned =
                                        item.type === "company"
                                            ? isSingle
                                                ? assignedCompanyId === item.id
                                                : commonCompanyId === item.id
                                            : isSingle
                                              ? assigned?.find(
                                                    entry =>
                                                        entry.id === item.id
                                                ) !== undefined
                                              : commonCleanerIds.includes(
                                                    item.id
                                                )

                                    // 排他制御（Matsuriユーザーの場合のみ適用）:
                                    // - アサイン済みの業者は常に解除可能
                                    // - 業者がアサインされている場合: 清掃員を無効化、他の業者も無効化
                                    // - 清掃員がアサインされている場合: 業者を無効化
                                    // 業者ユーザーの場合は排他制御なし（自社清掃員を自由にアサイン可能）
                                    const currentCompanyId = isSingle
                                        ? assignedCompanyId
                                        : commonCompanyId
                                    const isDisabledByExclusion =
                                        canSelectCompany &&
                                        (item.type === "company"
                                            ? currentCompanyId !== item.id &&
                                              (hasAssignedCleaners ||
                                                  hasAssignedCompany)
                                            : hasAssignedCompany)

                                    return (
                                        <div key={`${item.type}-${item.id}`}>
                                            <Checkbox
                                                checked={isAssigned}
                                                disabled={
                                                    progress.has(progressKey) ||
                                                    isDisabledByExclusion
                                                }
                                                onChange={event => {
                                                    void handleAssignChange(
                                                        item,
                                                        event.currentTarget
                                                            .checked
                                                    )
                                                }}
                                            >
                                                <div
                                                    css={css`
                                                        display: flex;
                                                        flex-direction: row;
                                                        gap: 4px;
                                                        align-items: center;
                                                        max-width: 400px;
                                                    `}
                                                >
                                                    {item.type === "company" ? (
                                                        <>
                                                            <CompanyTag />
                                                            <div>
                                                                {item.name}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div>
                                                                {item.name} (
                                                                {item.email})
                                                            </div>
                                                            <CleanerTags
                                                                tags={item.tags}
                                                            />
                                                            {approvalRequestStatus ===
                                                                "Rejected" && (
                                                                <RejectedLabel />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </Checkbox>
                                        </div>
                                    )
                                })
                            })()}
                        </div>
                    </TabUI>
                </section>
            </div>
        </FieldGroup>
    )
}

export interface AssignPanelContextState {
    openPanel: (cleaning: AssignableCleaning) => void
    closePanel: () => void
    selectCleanings: (cleanings: AssignableCleaning[]) => void
    onUpdate: () => void
    setResetCheckbox: (fn: () => void) => void
}

export const AssignPanelContext = React.createContext<
    AssignPanelContextState | undefined
>(undefined)

export const useAssignPanelContext = () => {
    const ctx = React.useContext(AssignPanelContext)
    if (!ctx) {
        throw new Error(
            "useAssignPanelContext must be used within a AssignPanelContextProvider"
        )
    }

    return ctx
}
